import { observer } from 'mobx-react-lite'
import { Fade, Typography, useTheme } from '@mui/material'
import { AppPaper } from 'src/component/common/analog/molecule/AppPaper'
import { AppButton } from 'src/component/common/analog/molecule/button/AppButton'
import { SkillSet } from '../../../skill-assessment/model/skill-assessment-model'
import { SkillRatingGrid } from './SkillRatingGrid'
import { useLocalizationStore } from 'src/feature/localization/store/localization.store'

/**
 * Props for SkillCategoryCard component
 */
interface SkillCategoryCardProps {
  categoryId: string
  categoryName: string
  categoryDescription: string
  skills: SkillSet[]
  isAllSkillsRated: boolean
  errorMessage: string
  onRateClick: (categoryId: string) => void
}

/**
 * Displays a single skill category card with its skills and rating interface
 * Shows category information, skill grid, and action buttons
 */
export const SkillCategoryCard = observer(({
  categoryId,
  categoryName,
  categoryDescription,
  skills,
  isAllSkillsRated,
  errorMessage,
  onRateClick,
}: SkillCategoryCardProps) => {
  const theme = useTheme()
  const { translation } = useLocalizationStore()
  const translations = translation.pages.myTeam.rolesButton

  return (
    <AppPaper sx={styles.root}>
      <Typography variant='h1'>{categoryName}</Typography>
      <Typography variant='body1' sx={styles.description}>{categoryDescription}</Typography>
      <Fade in={!isAllSkillsRated} timeout={700} mountOnEnter unmountOnExit>
        <Typography sx={styles.errorMessage}>
          {errorMessage}
        </Typography>
      </Fade>
      <SkillRatingGrid
        skills={skills}
        isAllSkillsRated={isAllSkillsRated}
        defaultColor={theme.palette.background.dark}
      />
      <AppButton
        fontSize={14}
        variant='contained'
        sx={{ width: '10em' }}
        onClick={() => onRateClick(categoryId)}
      >
        {isAllSkillsRated ? translations.updateSkill() : translations.rateSkill()}
      </AppButton>
    </AppPaper>
  )
})

const styles = {
  root: {
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 20,
    padding: 20,
    borderRadius: '12px',
    border: 0,
  },
  description: {
    marginTop: '-15px',
    width: { xs: '100%', md: '75%', lg: '60%' },
    textAlign: 'center',
    color: 'text.secondary',
  },
  errorMessage: {
    fontSize: 14,
    width: { xs: '100%', md: '75%', lg: '60%' },
    textAlign: 'center',
    color: 'secondary.main',
  },
}
